import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";
import { api, API_BASE } from "../../lib/api";

// Reusable avatar: shows uploaded pic if `has_avatar` else initials.
export function Avatar({ userId, name, hasAvatar, version = 0, size = 40 }) {
  const px = `${size}px`;
  const initial = (name?.[0] || "?").toUpperCase();
  const src = hasAvatar && userId ? `${API_BASE}/avatar/${userId}?v=${version}` : null;
  return (
    <div
      className="rounded-full overflow-hidden bg-gradient-to-br from-amber-500/40 to-orange-600/40 border border-amber-500/30 flex items-center justify-center font-display font-black uppercase text-white"
      style={{ width: px, height: px, fontSize: `${Math.max(12, Math.round(size * 0.38))}px` }}
    >
      {src ? (
        <img
          src={src}
          alt={name || "avatar"}
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        initial
      )}
    </div>
  );
}

// Larger uploader for the profile page.
export function AvatarUploader({ user, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0); // local cache-bust after upload/delete

  const upload = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please pick a JPEG, PNG or WebP image.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image too large (max 2 MB).");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try {
      const r = await api.post("/avatar", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Avatar updated.");
      setTick((t) => t + 1);
      onChange?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/avatar");
      toast.success("Avatar removed.");
      setTick((t) => t + 1);
      onChange?.({ has_avatar: false });
    } catch (e) {
      toast.error("Remove failed");
    } finally {
      setBusy(false);
    }
  };

  const currentVersion = (user?.avatar_version || 0) + tick;
  const hasAvatar = Boolean(user?.has_avatar);

  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center font-display font-black text-4xl text-[#0B0F17] shadow-lg shadow-amber-500/30">
          {hasAvatar ? (
            <img
              src={`${API_BASE}/avatar/${user.id}?v=${currentVersion}`}
              alt={user.name}
              className="w-full h-full object-cover"
            />
          ) : (
            (user.name?.[0] || "?").toUpperCase()
          )}
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#0B0F17] border border-white/20 text-amber-400 flex items-center justify-center hover:text-amber-300 hover:border-amber-500/60"
          data-testid="avatar-upload-trigger"
          title="Upload new avatar"
        >
          <Camera className="w-4 h-4" />
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="avatar-upload-input"
        />
      </div>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="btn-ghost text-xs"
          data-testid="avatar-upload-button"
        >
          <Camera className="w-3.5 h-3.5 inline mr-1" />{busy ? "Uploading…" : hasAvatar ? "Change photo" : "Upload photo"}
        </button>
        {hasAvatar && (
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            className="text-xs text-rose-400 hover:text-rose-300 flex items-center gap-1"
            data-testid="avatar-remove-button"
          >
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        )}
        <span className="text-[10px] text-slate-500 font-mono-data">JPEG/PNG/WebP · max 2 MB</span>
      </div>
    </div>
  );
}
